import { useTranslation } from 'react-i18next';
import { X, Package, CheckCircle, AlertTriangle } from 'lucide-react';
import { useCompleteGRN } from '../../../api/mutations/purchasing.mutations';
import type { GoodsReceivedNote } from '../../../api/queries/purchasing.queries';
import { useAuthStore } from '../../../stores/auth.store';

interface GRNDetailModalProps {
  grn: GoodsReceivedNote;
  onClose: () => void;
  onRefresh: () => void;
}

export default function GRNDetailModal({ grn, onClose, onRefresh }: GRNDetailModalProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const completeGRN = useCompleteGRN(grn._id);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'pending_review':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'has_discrepancies':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'good':
        return 'text-green-600 bg-green-50';
      case 'damaged':
      case 'expired':
      case 'wrong_item':
      case 'quality_issue':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleComplete = async () => {
    if (!confirm(t('purchasing.confirmCompleteGRN'))) return;
    try {
      await completeGRN.mutateAsync(undefined);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Failed to complete GRN:', error);
    }
  };

  const canComplete = grn.status !== 'completed' &&
    (user?.role === 'HQ_PURCHASING' || user?.role === 'HQ_ADMIN' || user?.role === 'WAREHOUSE_STAFF');

  const pendingDecisions = grn.discrepancySummary?.pendingDecisions || 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-xl font-bold">{grn.grnNumber}</h2>
              <p className="text-sm text-muted-foreground">
                {typeof grn.poId === 'object' && 'poNumber' in grn.poId && `PO: ${grn.poId.poNumber}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded border text-xs ${getStatusColor(grn.status)}`}>
              {t(`purchasing.${grn.status}`)}
            </span>
            {grn.hasDiscrepancies && (
              <span className="px-2 py-1 rounded bg-yellow-50 text-yellow-700 text-xs flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {t('purchasing.hasDiscrepancies')}
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Summary Cards */}
            {grn.discrepancySummary && (
              <div className="grid grid-cols-4 gap-4">
                {grn.discrepancySummary.shortageCount > 0 && (
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="text-sm text-orange-600">{t('purchasing.shortages')}</div>
                    <div className="text-2xl font-bold text-orange-700">{grn.discrepancySummary.shortageCount}</div>
                  </div>
                )}
                {grn.discrepancySummary.excessCount > 0 && (
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="text-sm text-blue-600">{t('purchasing.excess')}</div>
                    <div className="text-2xl font-bold text-blue-700">{grn.discrepancySummary.excessCount}</div>
                  </div>
                )}
                {grn.discrepancySummary.damagedCount > 0 && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <div className="text-sm text-red-600">{t('purchasing.damaged')}</div>
                    <div className="text-2xl font-bold text-red-700">{grn.discrepancySummary.damagedCount}</div>
                  </div>
                )}
                {grn.discrepancySummary.pendingDecisions > 0 && (
                  <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                    <div className="text-sm text-yellow-600">{t('purchasing.pendingDecisions')}</div>
                    <div className="text-2xl font-bold text-yellow-700">{grn.discrepancySummary.pendingDecisions}</div>
                  </div>
                )}
              </div>
            )}

            {/* Key Info */}
            <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-secondary/50">
              <div>
                <p className="text-sm text-muted-foreground">{t('purchasing.receivedBy')}</p>
                <p className="font-medium">{grn.receivedBy.username}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('purchasing.receivedDate')}</p>
                <p className="font-medium">{formatDate(grn.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('purchasing.warehouse')}</p>
                <p className="font-medium">{grn.warehouseId?.name}</p>
              </div>
            </div>

            {/* Document Info */}
            {(grn.deliveryNoteNumber || grn.invoiceNumber) && (
              <div className="grid grid-cols-2 gap-4">
                {grn.deliveryNoteNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('purchasing.deliveryNoteNumber')}</p>
                    <p className="font-medium">{grn.deliveryNoteNumber}</p>
                  </div>
                )}
                {grn.invoiceNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('purchasing.invoiceNumber')}</p>
                    <p className="font-medium">{grn.invoiceNumber}</p>
                  </div>
                )}
              </div>
            )}

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('purchasing.receivedItems')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('purchasing.item')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.ordered')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.received')}</th>
                      <th className="text-left py-2 px-3">{t('purchasing.condition')}</th>
                      <th className="text-left py-2 px-3">{t('purchasing.batchNo')}</th>
                      <th className="text-left py-2 px-3">{t('purchasing.expiryDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grn.items.map((item) => {
                      const hasDiscrepancy = item.discrepancyType && item.discrepancyType !== 'none';
                      return (
                        <tr key={item._id} className={`border-t ${hasDiscrepancy ? 'bg-yellow-50' : ''}`}>
                          <td className="py-2 px-3">
                            {item.isFreeFormItem ? item.customItemName : item.ingredientId?.name}
                          </td>
                          <td className="py-2 px-3 text-right">{item.orderedQuantity} {item.unit}</td>
                          <td className="py-2 px-3 text-right">
                            <span className={item.receivedQuantity !== item.orderedQuantity ? 'text-orange-600 font-medium' : ''}>
                              {item.receivedQuantity} {item.unit}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-0.5 rounded text-xs ${getConditionColor(item.condition)}`}>
                              {t(`purchasing.${item.condition}`)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {item.supplierBatchNumber || '-'}
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {item.expiryDate ? formatDate(item.expiryDate) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes */}
            {grn.notes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('common.notes')}</h3>
                <p className="text-sm text-muted-foreground">{grn.notes}</p>
              </div>
            )}

            {/* Review Notes */}
            {grn.reviewNotes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('purchasing.reviewNotes')}</h3>
                <p className="text-sm text-muted-foreground">{grn.reviewNotes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.close')}
          </button>
          {canComplete && pendingDecisions === 0 && (
            <button
              onClick={handleComplete}
              disabled={completeGRN.isPending}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {completeGRN.isPending ? t('common.saving') : t('purchasing.completeGRN')}
            </button>
          )}
          {pendingDecisions > 0 && (
            <p className="text-sm text-orange-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t('purchasing.resolveDiscrepancies', { count: pendingDecisions })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
